// ============================================================================
// 📁 File: WorkerDrawer.jsx
// 🧭 Module: PPE → Resources → Worker Master
// 📝 Pattern:
//   - SAME AS ToolDrawer / SkillDrawer
//   - Drawer-based Create / Edit
//   - Auto lifecycle handling (UI never sends status_id)
// ============================================================================

import React, { useEffect, useState } from "react";
import { Drawer, Form, Input, Select, Button, Checkbox, message } from "antd";

import axiosClient from "../../../../../api/axiosClient";

// ============================================================================
// COMPONENT
// ============================================================================

const WorkerDrawer = ({ open, record, onClose, onSuccess }) => {
  const [form] = Form.useForm();

  const [skills, setSkills] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [machineGroups, setMachineGroups] = useState([]);
  const [saving, setSaving] = useState(false);

  const isEdit = !!record;

  // --------------------------------------------------------------------------
  // LOAD LOOKUPS
  // --------------------------------------------------------------------------

  const loadLookups = async () => {
    try {
      const [skillRes, deptRes, mgRes] = await Promise.all([
        axiosClient.get("/api/ppe/masters/skills"),
        axiosClient.get("/api/ppe/masters/shop-departments"),
        axiosClient.get("/api/ppe/masters/machine-groups"),
      ]);

      setSkills(skillRes.data?.data || []);
      setDepartments(deptRes.data?.data || []);
      setMachineGroups(mgRes.data?.data || []);
    } catch {
      message.error("Failed to load lookup data");
    }
  };

  // --------------------------------------------------------------------------
  // LIFECYCLE
  // --------------------------------------------------------------------------

  useEffect(() => {
    if (open) {
      loadLookups();

      if (record) {
        form.setFieldsValue({
          worker_code: record.worker_code,
          worker_name: record.worker_name,
          skill_id: record.skill_id,
          department_id: record.department_id,
          machine_group_id: record.machine_group_id,
          wages_per_hour: record.wages_per_hour,
          is_active: record.is_active,
        });
      } else {
        form.resetFields();
        form.setFieldsValue({
          is_active: true,
        });
      }
    }
  }, [open, record, form]);

  // --------------------------------------------------------------------------
  // SAVE HANDLER
  // --------------------------------------------------------------------------

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      if (isEdit) {
        await axiosClient.put(
          `/api/ppe/masters/workers/${record.id}`,
          values
        );
        message.success("Worker updated");
      } else {
        await axiosClient.post("/api/ppe/masters/workers", values);
        message.success("Worker created");
      }

      onSuccess?.();
      onClose();
    } catch (err) {
      message.error(
        err?.response?.data?.message || "Save failed"
      );
    } finally {
      setSaving(false);
    }
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  return (
    <Drawer
      title={isEdit ? "Edit Worker" : "Create Worker"}
      width={520}
      open={open}
      onClose={onClose}
      destroyOnClose
      footer={
        <div className="text-end">
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button
            type="primary"
            loading={saving}
            onClick={() => form.submit()}
          >
            Save
          </Button>
        </div>
      }
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
      >
        {/* WORKER CODE */}
        <Form.Item
          label="Worker Code"
          name="worker_code"
          rules={[{ required: true, message: "Worker code is required" }]}
        >
          <Input
            placeholder="Enter worker code"
            disabled={isEdit}
          />
        </Form.Item>

        {/* WORKER NAME */}
        <Form.Item
          label="Worker Name"
          name="worker_name"
          rules={[{ required: true, message: "Worker name is required" }]}
        >
          <Input placeholder="Enter worker name" />
        </Form.Item>

        {/* SKILL */}
        <Form.Item
          label="Skill"
          name="skill_id"
          rules={[{ required: true, message: "Skill is required" }]}
        >
          <Select
            placeholder="Select skill"
            showSearch
            optionFilterProp="children"
          >
            {skills.map((s) => (
              <Select.Option key={s.id} value={s.id}>
                {s.skill_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* DEPARTMENT */}
        <Form.Item
          label="Department"
          name="department_id"
          rules={[
            { required: true, message: "Department is required" },
          ]}
        >
          <Select
            placeholder="Select department"
            showSearch
            optionFilterProp="children"
          >
            {departments.map((d) => (
              <Select.Option key={d.id} value={d.id}>
                {d.name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* MACHINE GROUP (OPTIONAL) */}
        <Form.Item
          label="Machine Group"
          name="machine_group_id"
        >
          <Select
            placeholder="Select machine group (optional)"
            allowClear
            showSearch
            optionFilterProp="children"
          >
            {machineGroups.map((mg) => (
              <Select.Option key={mg.id} value={mg.id}>
                {mg.group_name}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        {/* WAGES */}
        <Form.Item
          label="Wages per Hour"
          name="wages_per_hour"
          rules={[
            { required: true, message: "Wages per hour is required" },
          ]}
        >
          <Input type="number" min={0} />
        </Form.Item>

        {/* ACTIVE (EDIT ONLY) */}
        {isEdit && (
          <Form.Item
            name="is_active"
            valuePropName="checked"
          >
            <Checkbox>Active</Checkbox>
          </Form.Item>
        )}
      </Form>
    </Drawer>
  );
};

export default WorkerDrawer;
